import { useMutation } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { z, ZodError } from 'zod';

import { FormModal, SubmitProps } from '@/components/form-modal';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { LoaderIcon } from '@/components/ui/loader-icon';
import { useToast } from '@/components/ui/hooks/use-toast';
import { imporSchedulesFromV1 } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { Button } from '@/components/ui/button';

const formSchema = z.object({});
type Form = z.infer<typeof formSchema>;
const EMPTY_ERRORS: string[] = [];

export function ImportSchedulesFromV1() {
  const { toast } = useToast();
  const [errorMessages, setErrorMessages] = useState<string[]>(EMPTY_ERRORS);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: imporSchedulesFromV1,
    mutationKey: ['SCHEDULES'],
  });

  const onSubmit = ({ closeModal }: SubmitProps<Form>) => {
    setErrorMessages(EMPTY_ERRORS);
    mutateAsync()
      .then((summary) => {
        if (summary.failures.length) {
          setErrorMessages(
            summary.failures.map(
              ({ schedule, error }) => `Failed to import schedule ${schedule}: ${error.userFacingMessage}`,
            ),
          );
        }
        if (summary.isCompletelySuccessful()) {
          toast({
            title: 'Import complete',
            description: `Successfully imported ${summary.actualTotal} schedule(s).`,
          });
          closeModal();
          return;
        }
        if (summary.isCompleteFailure()) {
          toast({
            title: 'Import failed',
            description: 'See the modal for more detailed error messages.',
          });
          return;
        }
        toast({
          variant: 'destructive',
          title: 'Import partially failed',
          description: (
            <>
              <p>
                Although importing succeeded for {summary.succeeded} schedule(s), some schedule could not be imported.
                See the modal for more detailed messages.
              </p>
              <ul>
                {summary.skips.map((s, index) => (
                  <li key={s.schedule + index}>{s.reason}</li>
                ))}
              </ul>
            </>
          ),
        });
      })
      .catch((error) => {
        const airServerError = airServerErrorFrom(error);
        const errorMessage = airServerError?.userFacingMessage || 'Unknown';

        try {
          const summary = JSON.parse(errorMessage) as ZodError;
          toast({
            title: 'Import schedule: internal error',
            description: summary.errors.map((e) => e.message).join(', '),
          });
        } catch (error) {
          toast({
            title: 'Import schedule: internal error',
            description: errorMessage,
          });
        }
      });
  };

  return (
    <FormModal
      modalTitle="Import schedules from V1 Airtable"
      formSchema={formSchema}
      onSubmit={onSubmit}
      confirmBtnLabel="Import"
      trigger={<Button variant={'secondary'}>Import schedules from V1</Button>}
      onClose={() => setErrorMessages(EMPTY_ERRORS)}
    >
      {() => {
        return (
          <>
            <div className="grid grid-cols-1 gap-2 py-4">
              <ol>
                <li>1. Confirm in Airtable that the schedules to import have their "Sync with V2" fields checked.</li>
                <li>
                  2. Remember that you can attempt to sync the same schedule again. If an existing schedule matches an
                  Airtable schedule record, their account is instead overwritten with the latest data from Airtable. No
                  new account would be created for this schedule.
                </li>
              </ol>
            </div>
            {isPending && <LoaderIcon />}
            {errorMessages.length > 0 && (
              <div className="w-full mb-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error while importing schedules from V1 Airtable</AlertTitle>
                  <AlertDescription>
                    {errorMessages.map((message, index) => (
                      <p key={index}>{message}</p>
                    ))}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </>
        );
      }}
    </FormModal>
  );
}
